import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useLang } from '../../contexts/LangContext';
import { permissionGroupsApi, PermissionGroup } from '../../api/permissionGroupsApi';

export interface WorkflowStepForm {
  id?: string;
  stepOrder: number;
  stepName: string;
  statusDescription: string;
  feedbackStatus: string;
  permissionGroupId: string;
  slaDuration: number;
  slaUnit: string;
  reminderDuration: number;
  reminderUnit: string;
  assigneeIds: string[];
}

interface StaffOption {
  id: string;
  name: string;
  email: string;
}

interface FeedbackWorkflowEditorProps {
  steps: WorkflowStepForm[];
  onChange: (steps: WorkflowStepForm[]) => void;
  buildingId: string;
}

const STATUS_OPTIONS = [
  'PENDING_CONFIRMATION',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'REJECTED',
  'CANCELLED',
];

const SLA_UNITS = ['MINUTES', 'HOURS', 'DAYS'];

const emptyStep = (order: number): WorkflowStepForm => ({
  stepOrder: order,
  stepName: '',
  statusDescription: '',
  feedbackStatus: 'PENDING_CONFIRMATION',
  permissionGroupId: '',
  slaDuration: 1,
  slaUnit: 'HOURS',
  reminderDuration: 10,
  reminderUnit: 'MINUTES',
  assigneeIds: [],
});

export const FeedbackWorkflowEditor: React.FC<FeedbackWorkflowEditorProps> = ({
  steps,
  onChange,
  buildingId,
}) => {
  const { t } = useLang();
  const token = localStorage.getItem('token');
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [staffByGroup, setStaffByGroup] = useState<Record<string, StaffOption[]>>({});

  useEffect(() => {
    permissionGroupsApi.getAll().then(setPermissionGroups).catch(console.error);
  }, []);

  useEffect(() => {
    const groupIds = Array.from(
      new Set(
        steps
          .map((step) => step.permissionGroupId)
          .filter((groupId): groupId is string => Boolean(groupId)),
      ),
    );
    groupIds.forEach((groupId) => {
      loadStaffForGroup(groupId);
    });
  }, [steps]);

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      PENDING_CONFIRMATION: t('feedbackStatusPendingConfirmation'),
      CONFIRMED: t('feedbackStatusConfirmed'),
      IN_PROGRESS: t('feedbackStatusInProgress'),
      COMPLETED: t('feedbackStatusCompleted'),
      REJECTED: t('feedbackStatusRejected'),
      CANCELLED: t('feedbackStatusCancelled'),
    };
    return map[status] || status;
  };

  const getUnitLabel = (unit: string) => {
    const map: Record<string, string> = {
      MINUTES: t('slaUnitMinutes'),
      HOURS: t('slaUnitHours'),
      DAYS: t('slaUnitDays'),
    };
    return map[unit] || unit;
  };

  const loadStaffForGroup = async (groupId: string) => {
    if (!groupId || staffByGroup[groupId] !== undefined) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/users/staff?permissionGroupId=${groupId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.ok) {
        const data = await response.json();
        setStaffByGroup((prev) => ({ ...prev, [groupId]: data }));
      }
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const updateStep = (index: number, patch: Partial<WorkflowStepForm>) => {
    const next = steps.map((step, i) => (i === index ? { ...step, ...patch } : step));
    onChange(next);
  };

  const addStep = () => {
    onChange([...steps, emptyStep(steps.length + 1)]);
  };

  const removeStep = (index: number) => {
    const next = steps
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, stepOrder: i + 1 }));
    onChange(next);
  };

  const toggleAssignee = (index: number, userId: string) => {
    const step = steps[index];
    const assigneeIds = step.assigneeIds.includes(userId)
      ? step.assigneeIds.filter((id) => id !== userId)
      : [...step.assigneeIds, userId];
    updateStep(index, { assigneeIds });
  };

  return (
    <div className="space-y-4">
      {steps.map((step, index) => {
        const staffOptions = step.permissionGroupId
          ? staffByGroup[step.permissionGroupId] || []
          : [];

        return (
          <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
                {step.stepOrder}
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={step.stepName}
                  onChange={(e) => updateStep(index, { stepName: e.target.value })}
                  placeholder={t('workflowStepName')}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  value={step.statusDescription}
                  onChange={(e) => updateStep(index, { statusDescription: e.target.value })}
                  placeholder={t('workflowStatusDescription')}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm md:col-span-2"
                />
                <select
                  value={step.feedbackStatus}
                  onChange={(e) => updateStep(index, { feedbackStatus: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {getStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => removeStep(index)}
                className="text-red-500 hover:text-red-700 p-1"
                disabled={steps.length <= 1}
              >
                <Trash2 size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 ml-11">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('workflowDepartment')}</label>
                <select
                  value={step.permissionGroupId}
                  onChange={(e) => {
                    const groupId = e.target.value;
                    updateStep(index, { permissionGroupId: groupId, assigneeIds: [] });
                    if (groupId) loadStaffForGroup(groupId);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">{t('selectPermissionGroup')}</option>
                  {permissionGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('workflowAssignees')}</label>
                <div className="border border-gray-300 rounded-md bg-white max-h-24 overflow-y-auto p-2 text-sm">
                  {!step.permissionGroupId ? (
                    <span className="text-gray-400 text-xs">{t('selectPermissionGroup')}</span>
                  ) : staffByGroup[step.permissionGroupId] === undefined ? (
                    <span className="text-gray-400 text-xs">{t('loading')}...</span>
                  ) : staffOptions.length === 0 ? (
                    <span className="text-gray-400 text-xs">{t('selectStaff')}</span>
                  ) : (
                    staffOptions.map((staff) => (
                      <label key={staff.id} className="flex items-center gap-2 py-0.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={step.assigneeIds.includes(staff.id)}
                          onChange={() => toggleAssignee(index, staff.id)}
                        />
                        <span>{staff.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('workflowSla')}</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={step.slaDuration}
                    onChange={(e) => updateStep(index, { slaDuration: parseFloat(e.target.value) || 0 })}
                    className="w-20 px-2 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <select
                    value={step.slaUnit}
                    onChange={(e) => updateStep(index, { slaUnit: e.target.value })}
                    className="flex-1 px-2 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    {SLA_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {getUnitLabel(unit)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('workflowSlaReminder')}</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={0}
                    value={step.reminderDuration}
                    onChange={(e) => updateStep(index, { reminderDuration: parseFloat(e.target.value) || 0 })}
                    className="w-20 px-2 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <select
                    value={step.reminderUnit}
                    onChange={(e) => updateStep(index, { reminderUnit: e.target.value })}
                    className="flex-1 px-2 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    {SLA_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {getUnitLabel(unit)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addStep}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
      >
        <Plus size={16} />
        {t('addWorkflowStep')}
      </button>
    </div>
  );
};

export default FeedbackWorkflowEditor;
